/**
 * Importar extracto: movimientos del banco que se PARECEN a uno apuntado a
 * mano (mismo importe a pocos días, importe parecido, o en otra cuenta).
 * El servidor solo da por repetida la misma línea del banco
 * (`matchOnlySameBankLine`); todo lo demás lo decide la persona: Gavi
 * pregunta por cada uno, «Es el mismo» o «Son distintos». Nunca se decide
 * sin preguntar: dos cobros iguales pueden ser dos cobros reales (regla de
 * Vadik, 27-oct-2026: «que pregunte siempre»).
 *
 * Caso real (amiga de Vadik, 27-oct-2026): apuntó a mano con su propio
 * concepto y categoría; al traer el extracto, varios quedaron duplicados.
 */
import * as monthUtils from '@actual-app/core/shared/months';
import { amountToInteger } from '@actual-app/core/shared/util';

/** Un movimiento del extracto que va a entrar como nuevo. */
export type NuevoDelBanco = {
  id: string;
  fecha: string;
  /** En euros, como lo lee el extracto. */
  importe: number;
};

/** Un movimiento que ya estaba, apuntado a mano (no vino de un banco). */
export type Apuntado = {
  id: string;
  fecha: string;
  /** En céntimos, como la base de datos. */
  importe: number;
  cuenta: string;
  /** Para enseñarlo: comercio y categoría que le puso la persona. */
  payee?: string | null;
  categoria?: string | null;
  notas?: string | null;
};

export type Parecido = {
  /** Fila del extracto. */
  fila: string;
  /** El movimiento que ya estaba. */
  apuntado: Apuntado;
  motivo: 'importe' | 'fecha' | 'cuenta';
};

/** Días a cada lado para el mismo importe en la misma cuenta. */
export const DIAS_MISMO_IMPORTE = 15;
/** Días a cada lado para un importe parecido o para otra cuenta. */
export const DIAS_PARECIDO = 7;

/**
 * Diferencia de importe que aún se considera «parecido»: hasta 1 € o un 10 %
 * (redondear 12,35 a 12 o 47,80 a 50).
 */
export function importeParecido(banco: number, apuntado: number): boolean {
  if (banco === 0 || Math.sign(banco) !== Math.sign(apuntado)) {
    return false;
  }
  const diferencia = Math.abs(banco - apuntado);
  return diferencia <= Math.max(100, Math.abs(banco) * 0.1);
}

/** Fechas donde buscar lo apuntado: las del extracto con margen. */
export function rangoBusqueda(
  fechas: string[],
): { desde: string; hasta: string } | null {
  if (fechas.length === 0) {
    return null;
  }
  const orden = [...fechas].sort();
  return {
    desde: monthUtils.subDays(orden[0], DIAS_MISMO_IMPORTE),
    hasta: monthUtils.addDays(orden[orden.length - 1], DIAS_MISMO_IMPORTE),
  };
}

function dias(a: string, b: string): number {
  return Math.abs(monthUtils.differenceInCalendarDays(a, b));
}

/** Por qué se parecen, o null si no. */
export function motivoParecido(
  nuevo: NuevoDelBanco,
  apuntado: Apuntado,
  cuenta: string,
): Parecido['motivo'] | null {
  const banco = amountToInteger(nuevo.importe);
  const d = dias(nuevo.fecha, apuntado.fecha);
  const mismoImporte = banco === apuntado.importe;
  if (apuntado.cuenta !== cuenta) {
    return mismoImporte && d <= DIAS_PARECIDO ? 'cuenta' : null;
  }
  if (mismoImporte) {
    return d <= DIAS_MISMO_IMPORTE ? 'fecha' : null;
  }
  return d <= DIAS_PARECIDO && importeParecido(banco, apuntado.importe)
    ? 'importe'
    : null;
}

/**
 * Empareja cada movimiento nuevo con, como mucho, uno apuntado (y cada
 * apuntado con uno solo): primero los más parecidos (misma cuenta, menos
 * diferencia de importe, menos días).
 */
export function buscarParecidos(
  nuevos: NuevoDelBanco[],
  apuntados: Apuntado[],
  cuenta: string,
): Parecido[] {
  const candidatos: Array<Parecido & { peso: number }> = [];
  for (const nuevo of nuevos) {
    const banco = amountToInteger(nuevo.importe);
    for (const apuntado of apuntados) {
      const motivo = motivoParecido(nuevo, apuntado, cuenta);
      if (!motivo) {
        continue;
      }
      const peso =
        (apuntado.cuenta === cuenta ? 0 : 1_000_000) +
        Math.abs(banco - apuntado.importe) * 100 +
        dias(nuevo.fecha, apuntado.fecha);
      candidatos.push({ fila: nuevo.id, apuntado, motivo, peso });
    }
  }
  candidatos.sort((a, b) => a.peso - b.peso);
  const filas = new Set<string>();
  const usados = new Set<string>();
  const parecidos: Parecido[] = [];
  for (const { peso: _peso, ...c } of candidatos) {
    if (filas.has(c.fila) || usados.has(c.apuntado.id)) {
      continue;
    }
    filas.add(c.fila);
    usados.add(c.apuntado.id);
    parecidos.push(c);
  }
  return parecidos;
}

/**
 * «Es el mismo» (decisión de Vadik, 27-oct-2026): entra el del banco, que es
 * como vendrá las próximas veces (así Rumbo aprende su categoría), y sale el
 * apuntado. Lo de la persona no se pierde: su nombre (y sus notas) van a las
 * notas del del banco, y su categoría se queda si el banco no trae otra.
 */
export function fusionConApuntado(
  apuntado: Apuntado,
  nombreApuntado: string,
  categoriaDelBanco: string | null,
): { category: string | null; notes: string | null } {
  const notas = [nombreApuntado.trim(), apuntado.notas?.trim()]
    .filter(Boolean)
    .join(' · ');
  return {
    category: apuntado.categoria ?? categoriaDelBanco,
    notes: notas || null,
  };
}
